import { Link, useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useSettings } from "../context/SettingsContext";
import { fileUrl, formatPrice } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Minus, Plus, Trash2, ShoppingCart, ImageOff } from "lucide-react";
import { Seo } from "../lib/seo";

export default function Cart() {
  const { items, updateQuantity, removeItem, subtotal } = useCart();
  const { settings } = useSettings();
  const navigate = useNavigate();

  const threshold = settings?.free_shipping_threshold ?? 60;
  const shipping = subtotal >= threshold ? 0 : (settings?.shipping_cost ?? 6);
  const total = subtotal + shipping;

  if (items.length === 0) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-24 text-center">
        <Seo title="Carrello | Herbalife Rossano" description="Il tuo carrello." noindex />
        <ShoppingCart className="w-14 h-14 text-slate-300 mx-auto" />
        <h1 className="mt-4 text-2xl font-bold font-display text-slate-900">Il carrello è vuoto</h1>
        <Link to="/catalogo"><Button className="mt-6 rounded-full" data-testid="cart-goto-catalog">Vai al catalogo</Button></Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-6 py-12">
      <Seo title="Carrello | Herbalife Rossano" description="Il tuo carrello." noindex />
      <h1 className="text-3xl font-extrabold font-display text-slate-900">Carrello</h1>
      <div className="mt-8 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-4">
          {items.map((it) => (
            <Card key={it.product_id} className="p-4 rounded-2xl border-slate-200" data-testid={`cart-item-${it.product_id}`}>
              <div className="flex gap-4">
                <Link to={`/prodotto/${it.slug || it.product_id}`} data-testid={`cart-item-link-${it.product_id}`} className="w-20 h-20 rounded-xl bg-emerald-50 overflow-hidden flex items-center justify-center shrink-0">
                  {fileUrl(it.image_url) ? <img src={fileUrl(it.image_url)} alt={it.name} className="w-full h-full object-cover" /> : <ImageOff className="w-6 h-6 text-emerald-200" />}
                </Link>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-slate-900 line-clamp-2 leading-snug">{it.name}</h3>
                  <p className="mt-0.5 text-sm text-slate-500">{formatPrice(it.price)} / cad.</p>
                  <div className="mt-3 flex items-center justify-between gap-3">
                    <div className="flex items-center rounded-full border border-slate-200">
                      <button onClick={() => updateQuantity(it.product_id, it.quantity - 1)} className="p-2.5 text-slate-600 hover:text-primary" data-testid={`cart-minus-${it.product_id}`}><Minus className="w-4 h-4" /></button>
                      <span className="w-8 text-center text-sm font-semibold" data-testid={`cart-qty-${it.product_id}`}>{it.quantity}</span>
                      <button onClick={() => updateQuantity(it.product_id, it.quantity + 1)} className="p-2.5 text-slate-600 hover:text-primary" data-testid={`cart-plus-${it.product_id}`}><Plus className="w-4 h-4" /></button>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-slate-900" data-testid={`cart-line-total-${it.product_id}`}>{formatPrice(it.price * it.quantity)}</span>
                      <button onClick={() => removeItem(it.product_id)} className="p-2 text-slate-400 hover:text-rose-500" data-testid={`cart-remove-${it.product_id}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>

        <Card className="p-6 rounded-2xl border-slate-200 h-fit" data-testid="cart-summary">
          <h3 className="font-display font-bold text-lg text-slate-900">Riepilogo</h3>
          <div className="mt-4 space-y-2 text-sm">
            <div className="flex justify-between text-slate-600"><span>Subtotale</span><span>{formatPrice(subtotal)}</span></div>
            <div className="flex justify-between text-slate-600"><span>Spedizione</span><span>{shipping === 0 ? "Gratuita" : formatPrice(shipping)}</span></div>
            {shipping > 0 && <p className="text-xs text-emerald-600">Aggiungi {formatPrice(threshold - subtotal)} per la spedizione gratuita.</p>}
            <div className="border-t border-slate-100 pt-3 flex justify-between font-bold text-slate-900 text-lg"><span>Totale</span><span data-testid="cart-total">{formatPrice(total)}</span></div>
          </div>
          <Button className="w-full mt-6 rounded-full py-6" onClick={() => navigate("/checkout")} data-testid="cart-checkout-btn">Procedi all'acquisto</Button>
          <Link to="/catalogo" className="block text-center text-sm text-slate-500 mt-3 hover:text-primary">Continua lo shopping</Link>
        </Card>
      </div>
    </div>
  );
}
